import React from "react";
import { Button } from "reactstrap";
import { creditSourceInvoices, formatInr, lineAmount, lineQty, lineRate, memoAudit, memoTotals, memoValueLines, standingMemoLines } from "./salesInvoice";
import { statusLabel } from "./salesMemoStatus";

type Props = { memo: any; onClose: () => void };

const SalesMemoDocument = ({ memo, onClose }: Props) => {
  const credit = memo.type === "CREDIT";
  const approval = memo.type === "APPROVAL";
  const lineState = (line: any) => (approval
    ? ({ RETURNED: "Returned", KEPT: "Kept" } as Record<string, string>)[line.lineStatus] || "Out"
    : line.lineStatus === "RETURNED" ? "Returned" : "Billed");
  const lines = standingMemoLines(memo.lines || []);
  // An approval memo totals what is still out; returned and kept rows stay listed with their status.
  const totals = memoTotals(memoValueLines(memo));
  const audit = memoAudit(memo);
  return <div className="sales-document-overlay" role="dialog" aria-modal="true" aria-label="Billing memo preview">
    <div className="sales-document">
      <div className="sales-document-actions">
        <Button className="btn-hastkala-outline" onClick={onClose}>Close</Button>
        <Button color="primary" onClick={() => window.print()}>Print memo</Button>
      </div>
      <p className="sales-document-kicker">{approval ? "Goods sent on approval · Not a sale · Not a tax invoice" : "HKP internal document · Not a tax invoice"}</p>
      <h1>{approval ? "APPROVAL MEMO" : credit ? "RETURN MEMO" : "BILLING MEMO"}</h1>
      <p><strong>{memo.memoCode}</strong> · {statusLabel(memo.status, memo.type)}</p>
      <div className="sales-document-parties">
        <div><strong>From</strong><p>{memo.orgName || "Studio"}</p><p>{memo.sellerAddress || ""}</p><p>{memo.sellerGstin ? `GSTIN ${memo.sellerGstin}` : ""}</p></div>
        <div><strong>To</strong><p>{memo.partyName || "Party pending"}</p><p>{memo.partyAddress || ""}</p><p>{memo.partyGstin ? `GSTIN ${memo.partyGstin}` : ""}</p></div>
      </div>
      {credit && creditSourceInvoices(memo).length ? <p>Against invoice: {creditSourceInvoices(memo).join(", ")}</p>
        : memo.invoiceNumber ? <p>External invoice: {memo.invoiceNumber}</p> : null}
      {memo.creditNoteNumber ? <p>External credit note: {memo.creditNoteNumber}</p> : null}
      {approval && memo.returnBy ? <p>Return or confirm by: {new Date(`${memo.returnBy}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p> : null}
      {approval && memo.zohoChallanNumber ? <p>Zoho delivery challan: {memo.zohoChallanNumber}</p> : null}
      <div className="geo-table-wrap"><table className="geo-table"><thead><tr>
        <th>Piece</th><th>HKP ID</th><th>Status</th><th>HSN</th><th>Qty</th><th>GST</th><th>Rate</th><th>Amount</th>
      </tr></thead><tbody>{lines.map((line: any) => <tr key={line.id}>
        <td><div className="sales-invoice-item">{line.thumbnail ? <img src={line.thumbnail} alt="" /> : null}{line.name || "Piece"}</div></td>
        <td>{line.productUniqueId || "—"}</td><td>{lineState(line)}</td><td>{line.hsnCode || "—"}</td><td>{lineQty(line)}</td>
        <td>{line.gstRate || "—"}</td><td>{formatInr(lineRate(line))}</td><td>{formatInr(lineAmount(line))}</td>
      </tr>)}</tbody></table></div>
      <p className="sales-document-total"><strong>{approval ? "Still out" : "Total"}: {formatInr(totals.total)}</strong></p>
      {audit.length ? <section className="sales-memo-audit"><h2>Audit trail</h2>
        {audit.map((revision: any, index: number) => <article key={index}>
          <p>Earlier version{revision.reason ? ` · ${revision.reason}` : ""}</p>
          <ul>{(revision.lines || []).map((line: any) => <li key={line.id || line.productUniqueId}>{line.name || line.productUniqueId} · {formatInr(lineAmount(line))}</li>)}</ul>
        </article>)}
      </section> : null}
      <p>Prepared by {memo.createdByName || "Studio staff"}</p>
    </div>
  </div>;
};

export default SalesMemoDocument;
